<template>
  <div style="margin-top: -4px;">
    <SearchToolsBar :headerString="getHeader" />

    <div>
      <PodContextMenu
        v-if="contextMenuPod"
        v-model="showContextMenu"
        :asSearchResult="true"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :item="contextMenuPod"
        :key="contextMenuX * contextMenuY"
      />
    </div>

    <v-container class="search-list">
      <div v-for="nodeItem in getPagedSearchResults" :key="nodeItem.id">
        <v-row>
          <v-col>
            <router-link v-if="nodeItem.parent && nodeItem.parent.parent" :to="'/node/' + nodeItem.parent.parent.id">
              {{ nodeItem.parent.parent.name }}
            </router-link>
            <span v-if="nodeItem.parent && nodeItem.parent.parent">/</span>

            <router-link v-if="nodeItem.parent" :to="'/node/' + nodeItem.parent.id">
              {{ nodeItem.parent.name }}
            </router-link>
            <span v-if="nodeItem.parent">/</span>

            <router-link v-if="nodeItem" :to="'/node/' + nodeItem.id">
              {{ nodeItem.name }}
            </router-link>
          </v-col>
        </v-row>

        <v-row v-if="nodeItem.pods && nodeItem.pods.length > 0">
          <v-col v-if="getNodeView === 'list'">
            <div v-for="podItem in nodeItem.pods" :key="podItem.id" role="list">
              <ListPodItem
                :item="podItem"
                :asSearchResult="true"
                @showContextMenu="showContextMenuEvent"
                style="min-height: 104px;"
                class="list-view-item list-view-table-row listitem-with-checkbox"
              />
            </div>
          </v-col>

          <v-col v-if="getNodeView === 'grid'">
            <div v-for="podItem in nodeItem.pods" :key="podItem.id">
              <GridPodItem
                :item="podItem"
                :asSearchResult="true"
                @showContextMenu="showContextMenuEvent"
                class="grid-view-item"
              />
            </div>
          </v-col>
        </v-row>
      </div>

      <v-row v-if="!updateInProgress">
        <v-col class="d-flex justify-center">
          <div style="width: fit-content;">
            {{ $t('routes.node.showingNumberOfFolders', [getPagedSearchResults.length, searchResults.length]) }}

            <v-progress-linear
              v-if="searchResults.length !== 0"
              v-show="getPagedSearchResults.length !== searchResults.length"
              height="4"
              indeterminate
            />
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import { useSearchStore } from '@/stores/SearchStore.js';
import SearchToolsBar from './components/SearchToolsBar.vue';
import ListPodItem from '../nodes2/components/nodeListView/ListPodItem.vue';
import GridPodItem from '../nodes2/components/nodeGridView/GridPodItem.vue';
import PodContextMenu from '../nodes2/components/contextMenus/PodContextMenu.vue';

export default {
  name: "SearchIndex",
  components: {
    SearchToolsBar,
    ListPodItem,
    GridPodItem,
    PodContextMenu,
  },
  data() {
    return {
      // Context menu options
      showContextMenu: false,
      contextMenuPod: null,
      contextMenuX: 0,
      contextMenuY: 0,

      updateInProgress: false,
      itemsPerPage: 2,
      showingItems: 0,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.checkIfLoadMoreItemsNeeded();
    });

    const loadItemsDebounced = _debounce(() => {
      // Get the current scroll position
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      // Get the visible height of the window
      const windowHeight = window.innerHeight;
      // Get the total height of the document
      const documentHeight = document.documentElement.scrollHeight;
      // Check if the user has scrolled to the bottom
      if (scrollTop + windowHeight >= documentHeight - 1) {
        this.loadMoreItems();
      }
    }, 500); // Adjust the debounce delay as needed (e.g., 200ms)

    window.onscroll = loadItemsDebounced;
    window.onresize = loadItemsDebounced;
  },
  beforeUnmount() {
    window.onscroll = null;
    window.onresize = null;
    this.resetShowingItems();
  },
  methods: {
    showContextMenuEvent(eventData) {
      this.showContextMenu = false;
      this.contextMenuPod = eventData.contextMenuPod;
      this.contextMenuX = eventData.contextMenuX;
      this.contextMenuY = eventData.contextMenuY;
      this.$nextTick(() => {
        this.showContextMenu = true;
      });
    },
    resetShowingItems() {
      this.showingItems = this.itemsPerPage;

      this.$nextTick(() => {
        this.checkIfLoadMoreItemsNeeded();
      });
    },
    loadMoreItems() {
      this.showingItems += this.itemsPerPage;
    },
    checkIfLoadMoreItemsNeeded() {
      let hasVerticalScrollbar = document.body.scrollHeight - 2 > window.innerHeight;

      const intervalId = setInterval(() => {
        if (hasVerticalScrollbar || this.getPagedSearchResults.length === this.searchResults.length || this.searchResults.length === 0) {
          clearInterval(intervalId);
        } else {
          this.loadMoreItems();
          hasVerticalScrollbar = document.body.scrollHeight - 2 > window.innerHeight;
        }
      }, 500);
    },
  },
  computed: {
    getNodeView() {
      // List view or GridView
      // Always list view for search results as a PO requirement.
      return 'list';
    },
    getSearchQuery() {
      const searchStore = useSearchStore();
      return searchStore.getSearchQuery;
    },
    getSearchZone() {
      const searchStore = useSearchStore();
      return searchStore.getSearchZone;
    },
    ALLSEARCHZONES() {
      const searchStore = useSearchStore();
      return searchStore.getAllSearchZones;
    },
    getHeader() {
      return `${this.$t('routes.search.searchTitle')}: ${this.getSearchQuery}`;
    },
    searchResults() {
      const searchStore = useSearchStore();
      return searchStore.getSearchResults;
    },
    getPagedSearchResults() {
      return this.searchResults.slice(0, this.showingItems);
    },
    items() {
      const nodes = this.searchResults.filter((x) => x.pods.length == 0);
      let pods = [];
      for (let i = 0; i < this.searchResults.length; i++) {
        pods = pods.concat(this.searchResults[i].pods);
      }

      if (this.getSearchZone == this.ALLSEARCHZONES.METADATA) {
        return pods;
      }

      return nodes.concat(pods);
    }
  }
};
</script>

<style>
.search-list {
  font-size: .875rem !important;
}

.search-list a {
  text-decoration: none;
  font-weight: 600;
}
</style>
